import React, { forwardRef } from "react";
import {
  HiArrowDownTray,
  HiArrowLeft,
  HiArrowLongLeft,
  HiArrowLongRight,
  HiArrowPath,
  HiArrowUpOnSquare,
  HiAdjustmentsHorizontal,
  HiCheckCircle,
  HiChatBubbleLeftRight,
  HiChevronDown,
  HiChevronUp,
  HiClock,
  HiCog6Tooth,
  HiEllipsisHorizontal,
  HiEye,
  HiOutlineFunnel,
  HiOutlineSquares2X2,
  HiMagnifyingGlass,
  HiMegaphone,
  HiOutlineBars3,
  HiPaperAirplane,
  HiPencilSquare,
  HiPlus,
  HiPlusCircle,
  HiTrash,
  HiXCircle,
} from "react-icons/hi2";
import "./MainButtonComponent.css";

const ICON_MAP = {
  back: HiArrowLeft,
  refresh: HiArrowPath,
  add: HiPlus,
  view: HiEye,
  edit: HiPencilSquare,
  update: HiPencilSquare,
  create: HiPlusCircle,
  delete: HiTrash,
  more: HiEllipsisHorizontal,
  cancel: HiXCircle,
  close: HiXCircle,
  save: HiCheckCircle,
  comments: HiChatBubbleLeftRight,
  submit: HiPaperAirplane,
  publish: HiMegaphone,
  upload: HiArrowUpOnSquare,
  search: HiMagnifyingGlass,
  export: HiArrowDownTray,
  download: HiArrowDownTray,
  filter: HiOutlineFunnel,
  grid: HiOutlineSquares2X2,
  list: HiOutlineBars3,
  expandDown: HiChevronDown,
  expandUp: HiChevronUp,
  prev: HiArrowLongLeft,
  next: HiArrowLongRight,
  waiting: HiClock,
  setup: HiCog6Tooth,
  override: HiAdjustmentsHorizontal,
};

const ACTION_STYLE_MAP = {
  back: "neutral",
  refresh: "primary",
  add: "success",
  view: "primary",
  edit: "primary",
  update: "primary",
  create: "success",
  delete: "danger",
  more: "neutral",
  cancel: "neutral",
  close: "neutral",
  save: "primary",
  comments: "primary",
  submit: "primary",
  publish: "primary",
  upload: "primary",
  search: "primary",
  export: "primary",
  download: "primary",
  filter: "neutral",
  grid: "neutral",
  list: "neutral",
  expandDown: "neutral",
  expandUp: "neutral",
  prev: "neutral",
  next: "neutral",
  waiting: "neutral",
  setup: "neutral",
  override: "primary",
};

const joinClasses = (...classes) => classes.filter(Boolean).join(" ");

const MainButton = forwardRef(
  (
    {
      action,
      icon,
      label,
      size = "md",
      tone,
      className,
      type = "button",
      disabled = false,
      loading = false,
      title,
      ...rest
    },
    ref
  ) => {
    const iconKey = icon || action;
    const Icon = ICON_MAP[iconKey] || null;
    const toneKey = tone || ACTION_STYLE_MAP[action] || "primary";
    const isDisabled = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        className={joinClasses(
          "mbtn",
          `mbtn--${size}`,
          `mbtn--${toneKey}`,
          loading ? "mbtn--loading" : "",
          action ? `mbtn--action-${action}` : "",
          !label ? "mbtn--icon-only" : "",
          className
        )}
        disabled={isDisabled}
        aria-busy={loading}
        title={title}
        {...rest}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm mbtn__spinner" aria-hidden="true" />
        ) : (
          Icon && <Icon className="mbtn__icon" aria-hidden="true" />
        )}
        {label && <span className="mbtn__label">{label}</span>}
      </button>
    );
  }
);

const createPresetButton = ({ action, icon, defaultLabel, tone, size }) => {
  const PresetButton = forwardRef(({ label, ...rest }, ref) => (
    <MainButton
      ref={ref}
      action={action}
      icon={icon}
      tone={tone}
      size={size}
      label={label ?? defaultLabel}
      {...rest}
    />
  ));

  PresetButton.displayName = `${action[0].toUpperCase()}${action.slice(1)}Button`;
  return PresetButton;
};

export const AddButton = createPresetButton({
  action: "add",
  icon: "add",
  tone: "success",
  defaultLabel: "Add",
  size: "md",
});

export const BackButton = createPresetButton({
  action: "back",
  icon: "back",
  tone: "neutral",
  defaultLabel: "",
  size: "sm",
});

export const RefreshButton = createPresetButton({
  action: "refresh",
  icon: "refresh",
  tone: "primary",
  defaultLabel: "Refresh",
  size: "sm",
});

export const ViewButton = createPresetButton({
  action: "view",
  icon: "view",
  tone: "primary",
  defaultLabel: "View",
  size: "sm",
});

export const EditButton = createPresetButton({
  action: "edit",
  icon: "edit",
  tone: "primary",
  defaultLabel: "Edit",
  size: "sm",
});

export const DeleteButton = createPresetButton({
  action: "delete",
  icon: "delete",
  tone: "danger",
  defaultLabel: "Delete",
  size: "sm",
});

export const MoreButton = createPresetButton({
  action: "more",
  icon: "more",
  tone: "neutral",
  defaultLabel: "More",
  size: "sm",
});

export const CancelButton = createPresetButton({
  action: "cancel",
  icon: "cancel",
  tone: "neutral",
  defaultLabel: "Cancel",
  size: "md",
});

export const CloseButton = createPresetButton({
  action: "close",
  icon: "close",
  tone: "neutral",
  defaultLabel: "Close",
  size: "md",
});

export const SaveButton = createPresetButton({
  action: "save",
  icon: "save",
  tone: "primary",
  defaultLabel: "Save",
  size: "md",
});

export const SubmitButton = createPresetButton({
  action: "submit",
  icon: "submit",
  tone: "primary",
  defaultLabel: "Submit",
  size: "md",
});

export const PublishButton = createPresetButton({
  action: "publish",
  icon: "publish",
  tone: "primary",
  defaultLabel: "Publish",
  size: "md",
});

export const UploadButton = createPresetButton({
  action: "upload",
  icon: "upload",
  tone: "primary",
  defaultLabel: "Upload",
  size: "md",
});

export const SearchButton = createPresetButton({
  action: "search",
  icon: "search",
  tone: "primary",
  defaultLabel: "Search",
  size: "md",
});

export const ExportButton = createPresetButton({
  action: "export",
  icon: "export",
  tone: "primary",
  defaultLabel: "Export",
  size: "md",
});

export const WaitingButton = createPresetButton({
  action: "waiting",
  icon: "waiting",
  tone: "neutral",
  defaultLabel: "",
  size: "md",
});

export const RetireButton = createPresetButton({
  action: "retire",
  icon: "waiting",
  tone: "neutral",
  defaultLabel: "Retire",
  size: "md",
});

export default MainButton;
